// ---------------------------------------------------------------------------
// Nordic review flow — Time to First Review & Time to Merge (PRODOPSAI-674)
//
// Step-1 scope: three Nordic ERP teams, one repository (odevo-dev/nordic-portal),
// attribution by the PR author's GitHub team. Deliberately standalone — nothing
// here derives from `raw_dim_team`, `persons` or `team_memberships`. The whole
// feature is expected to be rebuilt when more teams need it.
// ---------------------------------------------------------------------------

/**
 * The two clocks the metrics can be measured on.
 *
 * - `office`   — 08:00–17:00 Mon–Fri, Europe/Stockholm. Nights and weekends do
 *                not count. This is the clock the Nordic teams' own goal
 *                tracker uses, and the one their targets are calibrated to.
 * - `calendar` — wall-clock elapsed time. What DORA benchmarks assume, and what
 *                Skystone's existing `leadTime` metric uses.
 *
 * Named alias rather than an inline union because it appears in the filter, the
 * response and the API contract (SonarCloud S4323).
 */
export type NordicReviewClock = 'office' | 'calendar';

/** The two metrics rendered by the section. */
export type NordicReviewMetricKey = 'firstReview' | 'merge';

/**
 * The three teams in step-1 scope. These strings are the join key between the
 * seeded roster and `raw_dim_team.team_name`, which was verified byte-identical
 * for Nordic ERP — so team selection in the page's existing dropdown resolves
 * with a plain string comparison and no mapping table.
 */
export const NORDIC_REVIEW_TEAM_NAMES = [
  'Team Customer',
  'Team Financial',
  'Team Asset',
] as const;

export type NordicReviewTeamName = (typeof NORDIC_REVIEW_TEAM_NAMES)[number];

/**
 * Targets from the Nordic teams' goal tracker, in hours. They are calibrated
 * against the office-hours clock, so the UI must hide the target line when the
 * calendar clock is selected — on calendar they are unreachable and the chart
 * would read as permanent failure.
 */
export const NORDIC_REVIEW_TARGET_HOURS: Readonly<Record<NordicReviewMetricKey, number>> = {
  firstReview: 4,
  merge: 12,
};

/** The single repository in step-1 scope. */
export const NORDIC_REVIEW_REPO = 'odevo-dev/nordic-portal';

/** One seeded roster row: a GitHub account and the team it belongs to. */
export interface INordicReviewTeamMember {
  githubLogin: string;
  teamName: string;
  displayName: string;
}

/**
 * One bar in one chart: a period bucket for one team.
 *
 * `medianHours` and `p75Hours` are null when no PR in the bucket reached the
 * relevant event (e.g. no PR received a human review). `prCount` is the sample
 * size those statistics were computed over — not the team's total PR count,
 * which is larger whenever a PR never reached the end event.
 */
export interface INordicReviewBucket {
  bucketStart: string;
  teamName: string;
  medianHours: number | null;
  p75Hours: number | null;
  prCount: number;
}

/**
 * Per-team coverage for the roster cards below the charts. `prCount` counts PRs
 * authored by this team's members in the period; the denominator lives on the
 * response as `totalPrCount`.
 */
export interface INordicReviewTeamCoverage {
  teamName: string;
  members: string[];
  prCount: number;
}

/**
 * Response for one metric.
 *
 * `totalPrCount` is every PR in the repository that falls in the period by this
 * metric's own anchor — merged date for Time to Merge, opened date for Time to
 * First Review — including PRs authored by people outside the three teams. It
 * is the `y` in the "x of y" coverage line, and is deliberately scoped to the
 * repository rather than the product (Nordic ERP has 11 bridged repos and only
 * this one is ingested).
 */
export interface INordicReviewMetricResponse {
  metric: NordicReviewMetricKey;
  clock: NordicReviewClock;
  buckets: INordicReviewBucket[];
  coverage: INordicReviewTeamCoverage[];
  totalPrCount: number;
}

/** Query filter accepted by the review-flow endpoints. */
export interface INordicReviewFilter {
  from: string;
  to: string;
  clock: NordicReviewClock;
  teamName?: string;
  granularity?: string;
}
